"use client";

import { useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  getPaymentPage,
  checkoutPaymentPage,
  retryPaymentPage,
  cancelPagePayment,
  getPageSessionStatus,
} from "@/lib/api/payment-page";
import { trackPageEvent } from "@/lib/tracking";
import type {
  PageCheckoutParams,
  PageRetryParams,
} from "@/lib/api/payment-page.schema";

const POLLING_INTERVAL = 3000; // 3 seconds

export function usePaymentPage(slug: string) {
  return useQuery({
    queryKey: ["payment-page", slug],
    queryFn: async () => {
      const result = await getPaymentPage(slug);
      if (result.error) {
        throw new Error(result.error);
      }
      return result.data!;
    },
    enabled: !!slug,
    retry: false,
    staleTime: 60 * 1000,
  });
}

export function usePageCheckout(slug: string) {
  return useMutation({
    mutationFn: async (params: PageCheckoutParams) => {
      const result = await checkoutPaymentPage(slug, params);
      if (result.error) throw new Error(result.error);
      return result.data!;
    },
  });
}

export function usePageRetry(slug: string) {
  return useMutation({
    mutationFn: async (params: PageRetryParams) => {
      const result = await retryPaymentPage(slug, params);
      if (result.error) throw new Error(result.error);
      return result.data!;
    },
  });
}

export function usePageCancel(slug: string) {
  return useMutation({
    mutationFn: async (attemptId: string) => {
      const result = await cancelPagePayment(slug, attemptId);
      if (result.error) throw new Error(result.error);
      return result.data!;
    },
  });
}

// Polling hook for payment page sessions

export interface PagePollingCallbacks {
  onCompleted?: (redirectUrl?: string | null) => void;
  onFailed?: () => void;
  onExpired?: () => void;
  // The page does not know this reference
  onMissing?: () => void;
}

interface UsePagePollingOptions {
  slug: string;
  reference: string;
  enabled?: boolean;
  callbacks: PagePollingCallbacks;
}

export function usePagePolling({
  slug,
  reference,
  enabled = false,
  callbacks,
}: UsePagePollingOptions) {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const callbacksRef = useRef(callbacks);
  const missesRef = useRef(0);

  useEffect(() => {
    callbacksRef.current = callbacks;
  });

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const result = await getPageSessionStatus(slug, reference);

      if (result.error) {
        // Twice in a row, so one failed read does not send a payer back to pay again
        if (result.statusCode === 404 && ++missesRef.current >= 2) {
          stopPolling();
          callbacksRef.current.onMissing?.();
        }
        return;
      }
      missesRef.current = 0;

      const data = result.data;
      if (!data) return;

      switch (data.status) {
        case "completed":
          stopPolling();
          trackPageEvent(slug, {
            event: "payment_completed",
            session_ref: reference,
          });
          callbacksRef.current.onCompleted?.(data.redirect_url);
          break;
        case "failed":
        case "cancelled":
          stopPolling();
          trackPageEvent(slug, {
            event: "payment_failed",
            session_ref: reference,
          });
          callbacksRef.current.onFailed?.();
          break;
        case "expired":
          stopPolling();
          callbacksRef.current.onExpired?.();
          break;
        case "pending":
        case "active":
          break;
      }
    } catch {}
  }, [slug, reference, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    poll();
    pollingIntervalRef.current = setInterval(poll, POLLING_INTERVAL);
  }, [poll, stopPolling]);

  useEffect(() => {
    if (enabled && reference) {
      startPolling();
    } else {
      stopPolling();
    }
    return stopPolling;
  }, [enabled, reference, startPolling, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return { startPolling, stopPolling };
}
